/* eslint-disable simple-import-sort/imports */
import Card from 'components/Card';
import Container from 'components/Container/index.jsx';
import {
  motion,
  useInView,
  useMotionTemplate,
  // useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import calendarIcon from 'assets/icons/hero/calendar.svg';
// eslint-disable-next-line import/no-unresolved
import { dataLogoCarousel } from '../LogoCarousel/data-LogoCarousel';
import './animation.css';
import { dataHero } from './data-hero.jsx';
import {
  CalendarIconStyled,
  CardContainer,
  CardWrapper,
  Content,
  Description,
  EasyBrow,
  EasyBrowText,
  HeroButton,
  HeroWrapper,
  ImageHero,
  ImageWrapper,
  InfoWrapper,
  PowerIconStyled,
  RecommendedTitle,
  SectionHero,
  Title,
} from './styles.js';

const Hero = () => {
  const heroWrapperRef = useRef(null);
  const containerRef = useRef(null);
  const sectionRef = useRef(null);
  const theme = useTheme();
  // const shouldReduceMotion = useReducedMotion();
  // Force animations for the Hero label/title even if the OS/browser requests reduced motion.
  // This is intentionally opt-in for this section because the design requires visible motion.
  // const forceHeroMotion = true;
  // const reduceHeroMotion = shouldReduceMotion && !forceHeroMotion;

  const headingInView = useInView(containerRef, { once: true });
  const headingRaw = dataHero.heading;
  const headingMatch = headingRaw.match(/^(.*?)(\d+)(.*)$/);
  const headingPrefix = headingMatch?.[1] ?? headingRaw;
  const headingTarget = headingMatch
    ? Number.parseInt(headingMatch[2], 10)
    : null;
  const headingSuffix = headingMatch?.[3] ?? '';
  const [count, setCount] = useState(headingTarget ? 0 : null);
  const [flippedCard, setFlippedCard] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setFlippedCard(null);
      }
    };

    if (window.innerWidth >= 1024) {
      setFlippedCard(null);
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!flippedCard) return;
    if (window.innerWidth >= 1024) return;

    const timer = setTimeout(() => {
      setFlippedCard(null);
    }, 5000);

    return () => clearTimeout(timer);
  }, [flippedCard]);

  useEffect(() => {
    if (!headingInView) return;
    if (!headingTarget) return;

    const start = performance.now();
    const dur = 1800;
    let raf = 0;

    const tick = (t) => {
      const p = Math.min((t - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setCount(Math.floor(eased * headingTarget));
      if (p < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [headingInView, headingTarget]);

  const { scrollYProgress } = useScroll({
    target: heroWrapperRef,
    offset: ['start start', 'center start'],
  });

  const fadeRange = [0.55, 0.6];
  const springConfig = {
    stiffness: 160,
    damping: 26,
  };

  const bg1 = useTransform(
    scrollYProgress,
    [0.3, 1],
    [theme.colors.secondary, theme.colors.blue300]
  );
  const bg2 = useTransform(
    scrollYProgress,
    [0.3, 1],
    [theme.colors.secondary600, theme.colors.blue300]
  );

  const backgroundImage = useMotionTemplate`linear-gradient(135deg, ${bg1} 0%, ${bg2} 100%)`;

  const iconColor = useTransform(
    scrollYProgress,
    [0, 1],
    [theme.colors.secondary, theme.colors.defaultText]
  );

  const textColor = useTransform(
    scrollYProgress,
    [0.55, 0.6],
    [theme.colors.defaultWhite, theme.colors.defaultText]
  );

  const leftFirstCardOpacityRaw = useTransform(
    scrollYProgress,
    fadeRange,
    [1, 0]
  );
  const leftSecondCardOpacityRaw = useTransform(
    scrollYProgress,
    fadeRange,
    [0, 1]
  );

  const leftFirstCardOpacity = useSpring(leftFirstCardOpacityRaw, springConfig);
  const leftSecondCardOpacity = useSpring(
    leftSecondCardOpacityRaw,
    springConfig
  );

  const centerFirstCardOpacityRaw = useTransform(
    scrollYProgress,
    fadeRange,
    [1, 0]
  );
  const centerSecondCardOpacityRaw = useTransform(
    scrollYProgress,
    fadeRange,
    [0, 1]
  );

  const centerFirstCardOpacity = useSpring(
    centerFirstCardOpacityRaw,
    springConfig
  );
  const centerSecondCardOpacity = useSpring(
    centerSecondCardOpacityRaw,
    springConfig
  );

  const rightFirstCardOpacityRaw = useTransform(
    scrollYProgress,
    fadeRange,
    [1, 0]
  );
  const rightSecondCardOpacityRaw = useTransform(
    scrollYProgress,
    fadeRange,
    [0, 1]
  );

  const rightFirstCardOpacity = useSpring(
    rightFirstCardOpacityRaw,
    springConfig
  );
  const rightSecondCardOpacity = useSpring(
    rightSecondCardOpacityRaw,
    springConfig
  );

  const image1OpacityRaw = useTransform(scrollYProgress, [0.8, 0.9], [1, 0]);
  const image2OpacityRaw = useTransform(scrollYProgress, [0.6, 0.65], [0, 1]);

  const image1Opacity = useSpring(image1OpacityRaw, springConfig);
  const image2Opacity = useSpring(image2OpacityRaw, springConfig);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
        delayChildren: 0.2,
      },
    },
  };

  const imageVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 20,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 20,
      },
    },
  };

  const handleCardClick = (position) => {
    if (window.innerWidth >= 1024) {
      return;
    }

    if (flippedCard === position) {
      setFlippedCard(null);
    } else {
      setFlippedCard(position);
    }
  };

  return (
    <HeroWrapper ref={heroWrapperRef} className="heroMotionAlways">
      <SectionHero ref={sectionRef} style={{ backgroundImage }}>
        <Container className="invoice__container" ref={containerRef}>
          <Content>
            <div style={{ position: 'relative', display: 'inline-flex' }}>
              {/* pulse layer */}
              {/* <motion.span
                aria-hidden="true"
                className="heroPulseLayer"
                style={{
                  position: 'absolute',
                  inset: -2, // controls how far it can grow
                  borderRadius: 9999,
                  zIndex: 0,
                  pointerEvents: 'none',
                }}
                animate={
                  reduceHeroMotion
                    ? { opacity: 0.22, scale: 1, filter: 'blur(6px)' }
                    : {
                        opacity: [0, 0.32, 0],
                        scale: [1, 1.14, 1],
                        filter: ['blur(6px)', 'blur(8px)', 'blur(6px)'],
                      }
                }
                transition={
                  reduceHeroMotion
                    ? { duration: 0 }
                    : {
                        duration: 4.2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }
                }
              /> */}
              <EasyBrow
                className="heroPillPulse"
                style={{ position: 'relative', zIndex: 1 }}
              >
                <PowerIconStyled
                  width="9"
                  height="12"
                  viewBox="0 0 9 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ color: iconColor }}
                >
                  <path
                    d="M5.43667 0.291674C5.43667 0.174746 5.36684 0.0691194 5.25925 0.0233162C5.15167 -0.0224871 5.02713 0.000387027 4.94284 0.081431L3.9057 1.07868C2.36149 2.5635 1.05667 4.27863 0.0376868 6.163C0.0136954 6.20537 0 6.25434 0 6.30651C0 6.46759 0.130584 6.59817 0.291667 6.59817H2.80583V10.7917C2.80583 10.9072 2.87399 11.0118 2.97964 11.0585C3.08529 11.1051 3.20853 11.0851 3.29391 11.0073L3.75796 10.5846C5.38238 9.10484 6.76093 7.376 7.84189 5.46293L8.12893 4.95493C8.17996 4.86463 8.17919 4.75403 8.12691 4.66445C8.07464 4.57486 7.97872 4.51978 7.875 4.51978H5.43667V0.291674Z"
                    fill="currentColor"
                  />
                </PowerIconStyled>
                <EasyBrowText
                  className="text heroPillTextPulse"
                  style={{ color: iconColor }}
                >
                  {headingTarget ? (
                    <>
                      {headingPrefix}
                      <span className="heroCount">{count ?? 0}</span>
                      {headingSuffix}
                    </>
                  ) : (
                    dataHero.heading
                  )}
                </EasyBrowText>
              </EasyBrow>
            </div>
            <Title
              as={motion.h1}
              className="heroTitlePulse"
              style={{ color: textColor }}
            >
              {dataHero.title}
            </Title>
            <Description as={motion.span} style={{ color: textColor }}>
              {dataHero.description}
            </Description>
            <HeroButton href={dataHero.button.href} target="_blank">
              <CalendarIconStyled src={calendarIcon} alt="Calendar" />
              {dataHero.button.title}
            </HeroButton>

            <InfoWrapper
              initial="hidden"
              animate="visible"
              variants={containerVariants}
            >
              <ImageWrapper $mobile variants={imageVariants}>
                <motion.div
                  style={{
                    opacity: image1Opacity,
                  }}
                >
                  <ImageHero
                    src={dataHero.images.image2}
                    alt="Fox"
                    loading="lazy"
                  />
                </motion.div>
                <motion.div
                  style={{
                    opacity: image2Opacity,
                  }}
                >
                  <ImageHero
                    src={dataHero.images.image1}
                    alt="Fox"
                    loading="lazy"
                  />
                </motion.div>
              </ImageWrapper>
              <CardWrapper $position="left" variants={cardVariants}>
                <CardContainer>
                  <motion.div
                    style={{
                      opacity: leftFirstCardOpacity,
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                    }}
                  >
                    <Card
                      variant="testimonial"
                      data={dataHero.testimonials[0]}
                    />
                  </motion.div>
                  <motion.div
                    style={{
                      opacity: leftSecondCardOpacity,
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                    }}
                  >
                    <Card
                      variant="tip"
                      data={dataHero.tips[0]}
                      isFlipped={flippedCard === 'left'}
                      onCardClick={() => handleCardClick('left')}
                    />
                  </motion.div>
                </CardContainer>
              </CardWrapper>

              <CardWrapper $position="center" variants={cardVariants}>
                <CardContainer>
                  <motion.div
                    style={{
                      opacity: centerFirstCardOpacity,
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                    }}
                  >
                    <Card
                      variant="testimonial"
                      data={dataHero.testimonials[1]}
                    />
                  </motion.div>
                  <motion.div
                    style={{
                      opacity: centerSecondCardOpacity,
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                    }}
                  >
                    <Card
                      variant="tip"
                      data={dataHero.tips[1]}
                      isFlipped={flippedCard === 'center'}
                      onCardClick={() => handleCardClick('center')}
                    />
                  </motion.div>
                </CardContainer>
              </CardWrapper>
              <CardWrapper $position="right" variants={cardVariants}>
                <CardContainer>
                  <motion.div
                    style={{
                      opacity: rightFirstCardOpacity,
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                    }}
                  >
                    <Card
                      variant="testimonial"
                      data={dataHero.testimonials[2]}
                    />
                  </motion.div>
                  <motion.div
                    style={{
                      opacity: rightSecondCardOpacity,
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                    }}
                  >
                    <Card
                      variant="tip"
                      data={dataHero.tips[2]}
                      isFlipped={flippedCard === 'right'}
                      onCardClick={() => handleCardClick('right')}
                    />
                  </motion.div>
                </CardContainer>
              </CardWrapper>
            </InfoWrapper>
          </Content>
        </Container>
        <RecommendedTitle>{dataLogoCarousel.title}</RecommendedTitle>
      </SectionHero>
    </HeroWrapper>
  );
};

export default Hero;
